/**
 * Authentication, tenancy, and administration API contract (PLAN §17.1, §17).
 * Implemented by services/internal/authserver and adminserver; consumed by apps/web.
 *
 * Auth model: browser sessions use an opaque, httpOnly, SameSite=Lax cookie
 * `backstory_session`; API clients use `Authorization: Bearer <token>`. CSRF: every
 * state-changing cookie-authenticated request must send `X-Backstory-CSRF` equal to the
 * `backstory_csrf` cookie (double submit).
 *
 * Public (no principal):
 *   POST /auth/signup/start             { email, plan? } -> { ok } (emails a link; no account yet)
 *   GET  /auth/signup/pending?token=    { email, plan }
 *   POST /auth/signup                   SignupRequest -> AuthResult (hosted signup requires the link; dev and invites may send a password directly)
 *   POST /auth/login                    LoginRequest -> AuthResult | MfaChallenge
 *   POST /auth/mfa/verify               MfaVerifyRequest -> AuthResult
 *   POST /auth/magic-link               { email } -> { ok: true }   (always 200)
 *   GET  /auth/magic-link/consume?token= -> redirect or AuthResult
 *   POST /auth/verify-email             { token } -> AuthResult
 *   POST /auth/password/forgot          { email } -> { ok: true }
 *   POST /auth/password/reset           { token, password } -> { ok: true }
 *   GET  /auth/oauth/{provider}/start   provider = google|microsoft|github -> 302
 *   GET  /auth/oauth/{provider}/callback -> 302 to app
 *   GET  /auth/sso/{connectionId}/start -> 302 to IdP (SAML/OIDC)
 *   POST /auth/sso/{connectionId}/acs   SAML ACS
 *   GET  /auth/sso/{connectionId}/callback OIDC callback
 *   GET  /auth/sso/lookup?email=        -> { connectionId?, requireSso: boolean }
 *   POST /auth/passkeys/login/options   -> WebAuthn request options
 *   POST /auth/passkeys/login/verify    -> AuthResult
 *   POST /auth/logout
 *
 * Authenticated (cookie or token), under /v1:
 *   GET  /v1/me                         -> Me
 *   PATCH /v1/me                        { name?, avatarUrl? } -> User
 *   POST /v1/me/password                { current, next }
 *   GET  /v1/me/mfa                     -> MfaStatus
 *   POST /v1/me/mfa/totp/start          -> { secret, otpauthUrl, qrSvg }
 *   POST /v1/me/mfa/totp/confirm        { code } -> { recoveryCodes: string[] }
 *   DELETE /v1/me/mfa/totp              { password } or, with no password, { code } or a passkey assertion
 *   POST /v1/me/passkeys/register/options -> WebAuthn creation options
 *   POST /v1/me/passkeys/register/verify  -> Passkey
 *   POST /v1/me/passkeys/step-up/options  -> WebAuthn request options, for confirming a removal
 *   DELETE /v1/me/passkeys/{id}           same confirmation body as turning off the authenticator app
 *   GET  /v1/me/sessions                -> LoginSession[]
 *   DELETE /v1/me/sessions              signs out every session except this one
 *   DELETE /v1/me/sessions/{id}
 *   GET  /v1/orgs                       -> Organization[] (memberships of the caller)
 *   POST /v1/orgs                       { name, slug } -> Organization
 *   GET  /v1/orgs/{orgId}               -> Organization
 *   PATCH /v1/orgs/{orgId}              OrgSettingsPatch
 *   GET  /v1/orgs/{orgId}/members       -> Member[]
 *   PATCH /v1/orgs/{orgId}/members/{userId} { role }
 *   DELETE /v1/orgs/{orgId}/members/{userId}
 *   GET  /v1/orgs/{orgId}/invitations   -> Invitation[]
 *   POST /v1/orgs/{orgId}/invitations   { email, role, projectIds? } -> Invitation
 *   DELETE /v1/orgs/{orgId}/invitations/{id}
 *   POST /auth/invitations/{token}/accept (public; creates or links account)
 *   GET  /v1/orgs/{orgId}/projects      -> ProjectFull[]
 *   POST /v1/orgs/{orgId}/projects      { name, privacyMode?, retentionDays?, allowedOrigins? } -> ProjectFull
 *   GET  /v1/projects/{projectId}       -> ProjectFull
 *   PATCH /v1/projects/{projectId}      ProjectPatch
 *   POST /v1/projects/{projectId}/rotate-key -> ProjectFull
 *   GET  /v1/orgs/{orgId}/tokens        -> ApiToken[]
 *   POST /v1/orgs/{orgId}/tokens        { name, scopes, projectId?, expiresAt? } -> ApiTokenCreated (secret shown once)
 *   DELETE /v1/orgs/{orgId}/tokens/{id}
 *   GET  /v1/orgs/{orgId}/audit?cursor&limit&action&actor -> Page<AuditEntry>
 *   GET  /v1/orgs/{orgId}/sso           -> SsoConnection[]
 *   POST /v1/orgs/{orgId}/sso           SsoConnectionCreate -> SsoConnection
 *   PATCH /v1/orgs/{orgId}/sso/{id}
 *   DELETE /v1/orgs/{orgId}/sso/{id}
 *   GET  /v1/orgs/{orgId}/domains       -> ClaimedDomain[]
 *   POST /v1/orgs/{orgId}/domains       { domain } -> ClaimedDomain (with TXT record to publish)
 *   POST /v1/orgs/{orgId}/domains/{id}/verify -> ClaimedDomain
 *   DELETE /v1/orgs/{orgId}/domains/{id}
 *
 * Project selection for session endpoints: cookie principals send `X-Backstory-Project: <projectId>`;
 * the server checks membership. API tokens are already bound to a project or org.
 */
export const ROLES = ["owner", "admin", "developer", "support", "readonly"];
export const ROLE_SCOPES = {
    owner: ["read", "write", "admin", "billing", "members", "tokens", "audit", "live", "ai"],
    admin: ["read", "write", "admin", "billing", "members", "tokens", "audit", "live", "ai"],
    developer: ["read", "write", "tokens", "ai"],
    support: ["read", "write", "live", "ai"],
    readonly: ["read"],
};
//# sourceMappingURL=api-auth.js.map